import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import placesData from '../components/data/SK.json';
import '../components/css/PlaceDetailsPage.css';
import Maps from '../components/Maps';
import Header from '../components/Header2';
import StarRating from '../components/StarRating';
import OpenStatus from '../components/OpenStatus';
import ResultsPage from './ResultsPage';
import N404 from '../components/N404';

const dayTranslations = {
  "Monday": "Pondelok",
  "Tuesday": "Utorok",
  "Wednesday": "Streda",
  "Thursday": "Štvrtok",
  "Friday": "Piatok",
  "Saturday": "Sobota",
  "Sunday": "Nedeľa",
};

function PlaceDetailsPage() {
  const { placeId } = useParams();
  const place = placesData?.find((p) => p.place_id === placeId) || null;

  const [mainImage, setMainImage] = useState(
    place?.short_photo_references?.length
      ? `/images/${place.short_photo_references[0]}.jpg`
      : '/logo_orig.png'
  );

  if (!place) {
    return <N404 /> ;
  }

  // --- Dynamic SEO ---
  const title = `${place.name} | ${place.search_city} | ${place.search_keyword}`;
  const description = `${place.name} – ${place.search_keyword} v meste ${place.search_city}. ⭐ Hodnotenie ${place.rating} (${place.user_ratings_total} recenzií). Adresa: ${place.address}.`;
  const keywords = `${place.name}, ${place.search_keyword}, ${place.search_city}, ${place.types.join(', ')}, ${place.rating} hviezdičiek, ${place.user_ratings_total} hodnotení, ${place.phone_number}`;
  const url = `https://www.sluzbomat.sk/catalog/${place.place_id}`;
  const image = mainImage || '/logo_orig.png';

  return (
    <>
      {/* --- SEO TAGS --- */}
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={url} />

        {/* Open Graph for social media */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
      </Helmet>

      <Header />
      <Maps places={place} />

      {place.distance !== undefined && place.direction !== undefined && (
        <ResultsPage distance={place.distance} direction={place.direction} />
      )}

      <div className="place-details-page">
        {/* Main Image and Thumbnails */}
        <div className="place-image" style={{ backgroundImage: `url(${mainImage})` }}>
          <div className="thumbnails">
            {place.photo_references?.slice(0, 4).map((photo, index) => (
              <div
                key={index}
                className="thumbnail"
                style={{ backgroundImage: `url(/images/${photo}.jpg)` }}
                onClick={() => setMainImage(`/images/${photo}.jpg`)}
              ></div>
            ))}
          </div>
        </div>

        <div className="spacer">
          {/* Place Details */}
          <div className="place-details">
            <h1>{place.name}</h1>
            <p>
              <a href={place.google_maps_url} target="_blank" rel="noopener noreferrer">
                {place.address.length > 40 ? `${place.address.substring(0, 40)}...` : place.address}
              </a>
            </p>
            <br />
            <div className="place-rating">
              <StarRating rating={place.rating} />
              <p>Hodnotilo: {place.user_ratings_total}</p>
            </div>

            {place.distance !== undefined && (
              <p>
                <i className="fas fa-map-marker-alt" aria-hidden="true"></i> {place.distance} metrov
                <span> {place.direction}</span>
              </p>
            )}

            <OpenStatus openingHours={place.opening_hours} />
          </div>

          <div className="place-phone">
            <a className="phone-number" href={`tel:${place.phone_number}`}>
              <i className="fa-solid fa-phone" id="phonespace"></i> {place.phone_number}
            </a>
          </div>

          {place.website && (
            <div className="place-website">
              <a className="website-link" target='_blank' href={place.website}>
                <i className="fa-solid fa-globe" id="webspace"></i> {place.website}
              </a>
            </div>
          )}
        </div>

        {/* Opening Hours Section */}
        <div className="opening-hours">
          <h3>Otváracie hodiny</h3>
          <ul>
            {place.opening_hours?.length > 0 ? (
              place.opening_hours.map((entry, index) => {
                const [day, hours] = entry.split(': ');
                const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

                const convertTo24Hour = (time) => {
                  if (!time) return '';
                  const match = time.match(/(\d{1,2}):?(\d{0,2})?\s?(AM|PM)?/i);
                  if (!match) return time;

                  let [_, hour, minute, period] = match;
                  hour = parseInt(hour, 10);
                  minute = minute ? minute.padStart(2, '0') : '00';

                  if (period) {
                    const isPM = period.toUpperCase() === 'PM';
                    if (isPM && hour !== 12) hour += 12;
                    if (!isPM && hour === 12) hour = 0;
                  }

                  return `${hour.toString().padStart(2, '0')}:${minute}`;
                };

                const convertedHours = hours
                  ?.replace(/\u2009/g, ' ')
                  .split(' – ')
                  .map(convertTo24Hour)
                  .join(' - ')
                  .replace('Closed', 'Zatvorené');

                return (
                  <li key={index} className={today === day ? 'today-opening-hours' : ''}>
                    <strong>{dayTranslations[day] || day}:</strong> {convertedHours || 'Zatvorené'}
                  </li>
                );
              })
            ) : (
              <li>Otváracie hodiny nie sú dostupné.</li>
            )}
          </ul>
        </div>

        {/* Price List */}
        {place.price_list && (
          <div className="price-list">
            <h3>Cenník</h3>
            <p>{place.price_list}</p>
          </div>
        )}
      </div>
    </>
  );
}

export default PlaceDetailsPage;
